/**
 * RevealScreen V11 — "A single believable life"
 * 
 * NOT tiles. NOT cards. ONE seamless living canvas.
 * 
 * - Aggressive radial masks dissolve ALL edges (fade from 15-25% inward)
 * - NO blur on mid/foreground — everything is CLEAR
 * - Deep background: very subtle blur only (1-2px) for depth atmosphere
 * - Dense overlap — images bleed into each other, no gaps
 * - Unified warm cinematic grade across everything
 * - Slow imperceptible breathing motion
 * - "My Vision" is a whisper, not an element
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const RevealScreen = ({ selectedImages, selectedWords, onEnterImmersion, onBack, layoutSeed }) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState(0);

  // Parallax
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const backX = useTransform(mouseX, [0, 1], [15, -15]);
  const backY = useTransform(mouseY, [0, 1], [10, -10]);
  const midX = useTransform(mouseX, [0, 1], [6, -6]);
  const midY = useTransform(mouseY, [0, 1], [4, -4]);
  const frontX = useTransform(mouseX, [0, 1], [2, -2]);
  const frontY = useTransform(mouseY, [0, 1], [1, -1]);

  useEffect(() => {
    const handler = (e) => {
      mouseX.set(e.clientX / window.innerWidth);
      mouseY.set(e.clientY / window.innerHeight);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, [mouseX, mouseY]);

  // Deterministic seeded random — uses layoutSeed for Memory Lock
  const seed = useMemo(() => layoutSeed || Math.random(), [layoutSeed]);
  const sr = useCallback((i) => {
    const x = Math.sin(seed * 1000 + i) * 10000;
    return x - Math.floor(x);
  }, [seed]);

  // ============================================
  // SEAMLESS CANVAS — one unified reality, every image shown once
  // ============================================
  const fieldItems = useMemo(() => {
    // 1. Dedupe by URL, preserve first-seen order.
    const seen = new Set();
    const unique = [];
    for (const img of selectedImages || []) {
      if (!img?.url || seen.has(img.url)) continue;
      seen.add(img.url);
      unique.push(img);
    }
    const total = unique.length;
    if (total === 0) return [];

    // 2. Interweave personal & aspirational for balance, then seeded shuffle.
    const personal = unique.filter((img) => img.isPersonal);
    const aspirational = unique.filter((img) => !img.isPersonal);
    const fused = [];
    const maxLen = Math.max(personal.length, aspirational.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < aspirational.length) fused.push(aspirational[i]);
      if (i < personal.length) fused.push(personal[i]);
    }
    const shuffled = [...fused];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(sr(i * 7) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // 3. Split into three layers — every image appears exactly ONCE across layers.
    //    Front = crisp anchors near the centre, Mid = dense collage ring,
    //    Deep = atmospheric grid behind everything.
    //
    //    DENSE CENTERED COLLAGE (Feb 2026): pulled the mid layer in tighter
    //    and added an inner anchor ring so the canvas reads as a packed
    //    collage hugging the "My Vision" card — not a wreath with empty
    //    space. The card sits in the geometric centre at z-index 24, and
    //    tiles wrap densely around it without overlapping the card body.
    const frontCount = Math.max(1, Math.min(total, total <= 3 ? total : total <= 7 ? 4 : total <= 14 ? 6 : 8));
    const remainingAfterFront = total - frontCount;
    const deepCount = remainingAfterFront > 12 ? Math.min(Math.ceil(remainingAfterFront * 0.35), 12) : 0;
    const midCount = remainingAfterFront - deepCount;

    const frontImgs = shuffled.slice(0, frontCount);
    const midImgs = shuffled.slice(frontCount, frontCount + midCount);
    const deepImgs = shuffled.slice(frontCount + midCount);

    // 4. Size scales inversely with density — fewer images ⇒ larger tiles, fuller canvas.
    const sizeScale = total <= 3 ? 1.65 : total <= 6 ? 1.4 : total <= 10 ? 1.18 : total <= 16 ? 1.02 : 0.9;

    const items = [];

    // --- DEEP LAYER: organic grid fill, subtle blur, atmospheric ---
    if (deepImgs.length) {
      const cols = deepImgs.length <= 4 ? 2 : deepImgs.length <= 9 ? 3 : 4;
      const rows = Math.ceil(deepImgs.length / cols);
      deepImgs.forEach((img, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cellW = 110 / cols;
        const cellH = 96 / rows;
        const baseX = col * cellW - 6 + sr(i * 11) * cellW * 0.3;
        const baseY = row * cellH - 4 + sr(i * 13) * cellH * 0.25;
        items.push({
          url: img.url,
          layer: 'deep',
          x: Math.max(-14, Math.min(88, baseX)),
          y: Math.max(-10, Math.min(80, baseY)),
          width: (28 + sr(i * 17) * 12) * sizeScale,
          rotation: (sr(i * 19) - 0.5) * 5,
          zIndex: 1 + Math.floor(sr(i * 23) * 2),
          opacity: 0.48 + sr(i * 29) * 0.18,
          blur: 0.6 + sr(i * 31) * 1.2,
          breathDur: 14 + sr(i * 37) * 6,
          breathDel: sr(i * 41) * 6,
        });
      });
    }

    // --- MID LAYER: dense inner ring of images packing tightly around the
    //    "My Vision" card. The card occupies roughly columns 38–62% × rows
    //    40–58%. We avoid that footprint while still putting tiles AS CLOSE
    //    as possible — no big empty halo around the card.
    if (midImgs.length) {
      // Two concentric rings of slots — inner (closer to card) + outer.
      // Inner radii are deliberately small (rx 18, ry 14) so tiles graze
      // the card edge without overlapping. Outer ring fills out to the
      // viewport edges.
      midImgs.forEach((img, i) => {
        const isInnerRing = i % 2 === 0 && i < Math.min(midImgs.length, 10);
        const angle = (i / Math.max(midImgs.length, 1)) * Math.PI * 2 + sr(i * 43) * 0.28;
        const rx = isInnerRing
          ? 21 + sr(i * 47) * 5      // inner ring — tight to card
          : 32 + sr(i * 49) * 10;    // outer ring — fills viewport
        const ry = isInnerRing
          ? 17 + sr(i * 53) * 4
          : 24 + sr(i * 55) * 8;
        const cx = 50 + Math.cos(angle) * rx - 11;
        const cy = 47 + Math.sin(angle) * ry - 8;
        const jX = (sr(i * 59) - 0.5) * 4;
        const jY = (sr(i * 61) - 0.5) * 3;
        items.push({
          url: img.url,
          layer: 'mid',
          x: Math.max(-8, Math.min(86, cx + jX)),
          y: Math.max(-6, Math.min(78, cy + jY)),
          width: (isInnerRing ? 18 + sr(i * 67) * 5 : 24 + sr(i * 67) * 8) * sizeScale,
          rotation: (sr(i * 71) - 0.5) * 3,
          zIndex: 7 + Math.floor(sr(i * 73) * 5),
          opacity: 0.86 + sr(i * 79) * 0.10,
          blur: 0,
          breathDur: 10 + sr(i * 89) * 4,
          breathDel: sr(i * 97) * 4,
        });
      });
    }

    // --- FRONT LAYER: crisp anchors that frame the card densely.
    //    Coordinates flank the card on all 8 cardinal positions so the
    //    central card is always surrounded — never floating alone.
    const frontPositions = [
      { x: 4, y: 4 },     // top-left
      { x: 68, y: 4 },    // top-right
      { x: 36, y: 2 },    // top-mid (above card)
      { x: 6, y: 60 },    // bottom-left
      { x: 70, y: 58 },   // bottom-right
      { x: 36, y: 68 },   // bottom-mid (below card)
      { x: 2, y: 32 },    // far-left (beside card)
      { x: 76, y: 32 },   // far-right (beside card)
    ];
    frontImgs.forEach((img, i) => {
      const pos = frontPositions[i % frontPositions.length];
      const jX = (sr(i * 101) - 0.5) * 4;
      const jY = (sr(i * 103) - 0.5) * 3;
      items.push({
        url: img.url,
        layer: 'front',
        x: pos.x + jX,
        y: pos.y + jY,
        width: (24 + sr(i * 107) * 6) * sizeScale,
        rotation: (sr(i * 109) - 0.5) * 2,
        zIndex: 20 + i,
        opacity: 0.94 + sr(i * 113) * 0.05,
        blur: 0,
        breathDur: 9 + sr(i * 127) * 3,
        breathDel: sr(i * 131) * 2,
        isHero: i === 0,
      });
    });

    return items;
  }, [selectedImages, sr]);

  // Word whispers — environmental, not UI
  const wordWhispers = useMemo(() => {
    const words = selectedWords.length > 0 ? selectedWords : [];
    const defaults = ['Love', 'Freedom', 'Abundance', 'Peace', 'Joy', 'Gratitude'];
    const all = [...new Set([...words, ...defaults])].slice(0, 12);

    return all.map((word, i) => {
      const x = 5 + sr(700 + i * 3) * 85;
      const y = 5 + sr(710 + i * 3) * 68;
      const sizeClass = sr(720 + i) > 0.7 ? 'large' : sr(720 + i) > 0.3 ? 'medium' : 'small';
      const fontSize = sizeClass === 'large' ? 'clamp(2.5rem, 5vw, 5rem)'
        : sizeClass === 'medium' ? 'clamp(1rem, 2vw, 2rem)'
        : 'clamp(0.7rem, 1vw, 0.9rem)';
      const opacity = sizeClass === 'large' ? 0.025 + sr(730 + i) * 0.03
        : sizeClass === 'medium' ? 0.04 + sr(730 + i) * 0.05
        : 0.06 + sr(730 + i) * 0.08;
      const rotation = (sr(740 + i) - 0.5) * 18;
      return { word, left: `${x}%`, top: `${y}%`, fontSize, opacity, rotation, sizeClass };
    });
  }, [selectedWords, sr]);

  // Group by layer
  const layers = useMemo(() => ({
    deep: fieldItems.filter(i => i.layer === 'deep'),
    mid: fieldItems.filter(i => i.layer === 'mid'),
    front: fieldItems.filter(i => i.layer === 'front'),
  }), [fieldItems]);

  // Transition — scale up, light wash, fade
  const handleEnterImmersion = useCallback(() => {
    setIsTransitioning(true);
    setTransitionPhase(1);
    setTimeout(() => setTransitionPhase(2), 600);
    setTimeout(() => setTransitionPhase(3), 1500);
    setTimeout(() => setTransitionPhase(4), 2500);
    setTimeout(() => onEnterImmersion(), 3500);
  }, [onEnterImmersion]);

  // ============================================
  // RENDER IMAGE — CLEAR, VIVID, ALIVE
  // Very gentle edge softening only — NO fog, NO heavy masks
  // ============================================
  const renderFieldImage = (item, index) => {
    const isDeep = item.layer === 'deep';
    const isMid = item.layer === 'mid';
    const isFront = item.layer === 'front';

    // GENTLE edge feathering — 70-80% of image fully visible
    // Only the outer 20-30% softly fades — creates blending without fog
    const maskImage = isDeep
      ? 'radial-gradient(ellipse 90% 90% at center, black 60%, transparent 100%)'
      : isMid
      ? 'radial-gradient(ellipse 88% 88% at center, black 65%, transparent 100%)'
      : 'radial-gradient(ellipse 92% 92% at center, black 72%, transparent 100%)';

    // Slightly varied organic aspect ratios
    const ar = sr(index * 200);
    const aspect = ar > 0.6 ? '5 / 4' : ar > 0.3 ? '4 / 5' : '1 / 1';
    
    // Unified warm grade — clear and vivid, NOT dimmed
    const warmth = 'sepia(0.04) saturate(1.05)';
    const depthFilter = isDeep
      ? `blur(${item.blur}px) ${warmth} brightness(0.7)`
      : isMid
      ? `${warmth} brightness(0.88) contrast(1.02)`
      : `${warmth} brightness(0.93) contrast(1.03)`;

    const breathAmp = isDeep ? 0.6 : isMid ? 1.5 : 0.8;
    const rotAmp = isDeep ? 0.15 : isMid ? 0.4 : 0.2;

    const tScale = transitionPhase >= 3 ? (isFront ? 1.12 : 0.96)
      : transitionPhase >= 2 ? 1.05
      : 1;

    return (
      <motion.div
        key={`${item.layer}-${index}-${item.url}`}
        className="absolute overflow-visible"
        style={{
          left: `${item.x}%`,
          top: `${item.y}%`,
          width: `${item.width}%`,
          aspectRatio: aspect,
          zIndex: item.zIndex,
          willChange: 'transform',
        }}
        initial={{ opacity: 0, scale: 0.97, rotate: item.rotation }}
        animate={isTransitioning ? {
          opacity: transitionPhase >= 4 ? 0 : item.opacity,
          scale: tScale,
          rotate: item.rotation,
          y: transitionPhase >= 3 ? (isFront ? -20 : 5) : 0,
        } : {
          opacity: item.opacity,
          scale: 1,
          rotate: [item.rotation - rotAmp, item.rotation + rotAmp, item.rotation - rotAmp],
          y: [breathAmp, -breathAmp, breathAmp],
        }}
        transition={isTransitioning ? {
          duration: 1.2,
          ease: [0.25, 0.46, 0.45, 0.94],
        } : {
          default: { delay: 0.02 + index * 0.003, duration: 0.6, ease: 'easeOut' },
          rotate: { duration: item.breathDur, repeat: Infinity, ease: 'easeInOut', delay: item.breathDel },
          y: { duration: item.breathDur, repeat: Infinity, ease: 'easeInOut', delay: item.breathDel },
        }}
      >
        <img
          src={item.url}
          alt=""
          className="w-full h-full object-cover"
          loading={isDeep ? 'lazy' : 'eager'}
          style={{
            filter: depthFilter,
            maskImage: maskImage,
            WebkitMaskImage: maskImage,
          }}
        />
      </motion.div>
    );
  };

  return (
    <motion.div
      className="fixed inset-0 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="reveal-screen"
    >
      {/* Base tone — deep warm dark */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #0a0e0b 0%, #080b08 50%, #060906 100%)',
      }} />

      {/* Very subtle warm ambient — NO center dimming */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-[1]"
        animate={{
          background: [
            'radial-gradient(ellipse 150% 130% at 50% 45%, rgba(90, 75, 50, 0.04) 0%, transparent 70%)',
            'radial-gradient(ellipse 160% 140% at 52% 48%, rgba(100, 80, 55, 0.06) 0%, transparent 70%)',
            'radial-gradient(ellipse 150% 130% at 50% 45%, rgba(90, 75, 50, 0.04) 0%, transparent 70%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Back button */}
      {!isTransitioning && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 z-[60] p-2 rounded-full"
          style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.05)' }}
          data-testid="reveal-back-btn"
        >
          <ArrowLeft size={16} className="text-white/30" />
        </button>
      )}

      {/* Word whispers — woven BETWEEN images, part of the field */}
      <motion.div
        className="absolute inset-0 overflow-visible z-[15] pointer-events-none"
        style={{ x: midX, y: midY }}
      >
        {wordWhispers.map((item, i) => (
          <motion.span
            key={item.word + i}
            className="absolute select-none"
            style={{
              left: item.left,
              top: item.top,
              fontFamily: "'Playfair Display', serif",
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: item.fontSize,
              color: `rgba(225, 215, 195, ${item.opacity})`,
              letterSpacing: item.sizeClass === 'large' ? '0.15em' : '0.06em',
              transform: `rotate(${item.rotation}deg)`,
              whiteSpace: 'nowrap',
              textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}
            initial={{ opacity: 0 }}
            animate={isTransitioning ? { opacity: 0 } : {
              opacity: [item.opacity * 0.4, item.opacity, item.opacity * 0.5],
            }}
            transition={isTransitioning ? { duration: 0.4 } : {
              delay: 1.5 + i * 0.3,
              duration: 12 + sr(800 + i) * 6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            {item.word}
          </motion.span>
        ))}
      </motion.div>

      {/* DEEP LAYER — atmosphere, subtle blur, fills every gap */}
      <motion.div
        className="absolute overflow-visible z-[2]"
        style={{ inset: '-15% -12% 0% -12%', x: backX, y: backY }}
      >
        {layers.deep.map((item, i) => renderFieldImage(item, i))}
      </motion.div>

      {/* MID LAYER — clear, no blur */}
      <motion.div
        className="absolute overflow-visible z-[10]"
        style={{ inset: '-8% -5% 5% -5%', x: midX, y: midY }}
      >
        {layers.mid.map((item, i) => renderFieldImage(item, i))}
      </motion.div>

      {/* FRONT LAYER — crisp anchors */}
      <motion.div
        className="absolute overflow-visible z-[22]"
        style={{ inset: '-3% 0% 8% 0%', x: frontX, y: frontY }}
      >
        {layers.front.map((item, i) => renderFieldImage(item, i))}
      </motion.div>

      {/* "My Vision" — a glass card anchored at the exact heart of the canvas */}
      <motion.div
        className="absolute z-[24] pointer-events-none"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={isTransitioning ? {
          opacity: transitionPhase >= 3 ? 0 : 0.95,
          scale: transitionPhase >= 3 ? 1.18 : 1,
        } : {
          opacity: 1,
          scale: [1, 1.018, 1],
        }}
        transition={isTransitioning ? { duration: 1 } : {
          delay: 1.6,
          opacity: { duration: 1.4, ease: 'easeOut' },
          scale: { duration: 7, repeat: Infinity, ease: 'easeInOut' },
        }}
        data-testid="my-vision-card"
      >
        {/* Outer halo — soft warm glow that bleeds beyond the pill, matching
            the user's reference layout where the card looks lit-from-within. */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(220,185,130,0.22) 0%, rgba(200,170,120,0.12) 35%, transparent 75%)',
            filter: 'blur(28px)',
            transform: 'scale(1.65)',
          }}
          animate={{ opacity: [0.55, 0.85, 0.55] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="relative px-7 py-4 md:px-10 md:py-5 rounded-2xl"
          style={{
            background: 'linear-gradient(140deg, rgba(28,22,14,0.62) 0%, rgba(18,14,9,0.5) 50%, rgba(28,22,14,0.6) 100%)',
            border: '1px solid rgba(210, 180, 130, 0.32)',
            backdropFilter: 'blur(18px) saturate(1.05)',
            WebkitBackdropFilter: 'blur(18px) saturate(1.05)',
            boxShadow:
              '0 18px 60px rgba(0,0,0,0.55), 0 0 120px rgba(220,185,130,0.22), inset 0 1px 0 rgba(255,240,210,0.10)',
          }}
          animate={{
            boxShadow: [
              '0 18px 60px rgba(0,0,0,0.55), 0 0 100px rgba(210,175,125,0.18), inset 0 1px 0 rgba(255,240,210,0.08)',
              '0 18px 60px rgba(0,0,0,0.55), 0 0 160px rgba(230,195,140,0.32), inset 0 1px 0 rgba(255,240,210,0.14)',
              '0 18px 60px rgba(0,0,0,0.55), 0 0 100px rgba(210,175,125,0.18), inset 0 1px 0 rgba(255,240,210,0.08)',
            ],
          }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* gold hairline accent */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-px"
            style={{
              width: '38%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(220,185,130,0.55) 50%, transparent 100%)',
            }}
          />
          <span
            className="block select-none text-center"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontStyle: 'italic',
              fontSize: 'clamp(1.6rem, 3.4vw, 2.6rem)',
              color: 'rgba(245, 235, 215, 0.96)',
              letterSpacing: '0.18em',
              textShadow: '0 2px 22px rgba(0,0,0,0.55), 0 0 38px rgba(210, 180, 120, 0.14)',
              lineHeight: 1.05,
            }}
          >
            My Vision
          </span>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-px"
            style={{
              width: '24%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(220,185,130,0.4) 50%, transparent 100%)',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Minimal bottom fade — just enough for text readability */}
      <div className="absolute left-0 right-0 bottom-0 z-[35] pointer-events-none" style={{
        height: '14%',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(6,9,6,0.35) 50%, rgba(5,7,5,0.8) 100%)',
      }} />

      {/* "Enter your vision" — emerges from the field */}
      <AnimatePresence>
        {!isTransitioning && (
          <motion.div
            className="absolute left-0 right-0 bottom-0 z-[40] flex items-end justify-center pb-8"
            style={{ height: '15%' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 2.5, duration: 2 }}
          >
            <motion.div
              className="cursor-pointer text-center"
              onClick={handleEnterImmersion}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="enter-immersion-btn"
            >
              <motion.span
                className="block select-none"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: 'italic',
                  fontSize: 'clamp(1.1rem, 1.8vw, 1.5rem)',
                  color: 'rgba(210, 200, 180, 0.5)',
                  letterSpacing: '0.2em',
                }}
                animate={{
                  textShadow: [
                    '0 0 30px rgba(180,150,100,0.15)',
                    '0 0 60px rgba(200,170,120,0.30)',
                    '0 0 30px rgba(180,150,100,0.15)',
                  ],
                  opacity: [0.4, 0.75, 0.4],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              >
                Enter your vision
              </motion.span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transition overlays — light wash → fade */}
      {isTransitioning && (
        <>
          <motion.div
            className="absolute inset-0 pointer-events-none z-[43]"
            initial={{ opacity: 0 }}
            animate={{ opacity: transitionPhase >= 2 ? 0.3 : 0 }}
            transition={{ duration: 1.5 }}
            style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 45%, rgba(255,250,235,0.12) 0%, transparent 60%)' }}
          />
          <motion.div
            className="absolute inset-0 pointer-events-none z-[44]"
            initial={{ opacity: 0 }}
            animate={{ opacity: transitionPhase >= 2 ? 0.6 : 0 }}
            transition={{ duration: 1.2 }}
            style={{ background: 'radial-gradient(ellipse 45% 40% at 50% 42%, transparent 0%, rgba(4,6,4,0.9) 100%)' }}
          />
          <motion.div
            className="absolute inset-0 pointer-events-none z-[56]"
            initial={{ opacity: 0 }}
            animate={{ opacity: transitionPhase >= 4 ? 1 : 0 }}
            transition={{ duration: 1.2 }}
            style={{ background: '#030504' }}
          />
        </>
      )}
    </motion.div>
  );
};

export default RevealScreen;
